/**
 * <tss-category-bento> — text tabs that switch between server-rendered tile grids
 * (sections/category-bento.liquid). No fetching: every panel is in the page,
 * hidden ones just aren't shown, so their lazy images load on first view.
 *
 * The heading link follows the active tab. Swiping the tiles sideways moves to the
 * next / previous tab. Loaded from the section, so Motion is imported lazily.
 */

const SWIPE_MIN_DISTANCE = 50; // px

class CategoryBento extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[data-tabs-panel]')];
    this.viewLinks = [...this.querySelectorAll('[data-tabs-view]')];
    if (this.tabs.length < 2) return;

    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    this.querySelector('[role="tablist"]').addEventListener('keydown', (event) => this.onKeydown(event));
    this.panels.forEach((panel) => {
      panel.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
      panel.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
    });

    // Theme editor: selecting a tile inside a hidden tab opens that tab.
    this.onBlockSelect = (event) => {
      const panel = event.target.closest?.('[data-tabs-panel]');
      const tab = panel && this.tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id);
      const selectedTab = this.tabs.includes(event.target) ? event.target : tab;
      if (selectedTab && this.contains(event.target)) this.select(selectedTab);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  get selectedIndex() {
    return this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
  }

  onTouchStart(event) {
    const touch = event.touches[0];
    this.touchStart = { x: touch.clientX, y: touch.clientY };
  }

  // A clearly sideways swipe only, so vertical page scrolling is never caught.
  onTouchEnd(event) {
    if (!this.touchStart) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStart.x;
    const deltaY = touch.clientY - this.touchStart.y;
    this.touchStart = null;
    if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const step = deltaX < 0 !== isRtl ? 1 : -1;
    const nextTab = this.tabs[this.selectedIndex + step];
    if (!nextTab) return;

    nextTab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    this.select(nextTab, step);
  }

  onKeydown(event) {
    const currentIndex = this.tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    const lastIndex = this.tabs.length - 1;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let targetIndex = null;

    if (event.key === (isRtl ? 'ArrowLeft' : 'ArrowRight')) targetIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    if (event.key === (isRtl ? 'ArrowRight' : 'ArrowLeft')) targetIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = lastIndex;
    if (targetIndex === null) return;

    event.preventDefault();
    this.tabs.forEach((tab, index) => (tab.tabIndex = index === targetIndex ? 0 : -1));
    this.tabs[targetIndex].focus();
  }

  select(tab, slideFrom = 0) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.tabs.forEach((candidate) => {
      const isSelected = candidate === tab;
      candidate.setAttribute('aria-selected', String(isSelected));
      candidate.tabIndex = isSelected ? 0 : -1;
    });

    const activePanel = this.panels.find((panel) => panel.id === tab.getAttribute('aria-controls'));
    this.panels.forEach((panel) => (panel.hidden = panel !== activePanel));
    if (tab.dataset.url) this.viewLinks.forEach((link) => (link.href = tab.dataset.url));

    if (activePanel) this.slideIn(activePanel, slideFrom);
  }

  // Swipe-driven switches slide the new tiles in from the swipe side.
  async slideIn(panel, direction) {
    if (!direction || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      const { animate } = await import('@theme/motion');
      animate(panel, { opacity: [0, 1], x: [direction * 40, 0] }, { duration: 0.3, ease: 'easeOut' });
    } catch {
      // No animation is fine; the tiles have already swapped.
    }
  }
}

if (!customElements.get('tss-category-bento')) {
  customElements.define('tss-category-bento', CategoryBento);
}
